import { useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Box, Tooltip } from "@mui/material";
import { useOverlayTransform } from "./useOverlayTransform";

const PAD = 24;
const MIN_R = 20;
// The radius cap is expressed in cm, not px: a fixed px cap silently became a 5 cm ceiling at the
// app's 40 px/cm scale, so constructions like "AB = 6cm, BC = 8cm, AC = 10cm" were undrawable.
// 40 px/cm is VirtualRulerOverlay's default, so both tools agree when pixels_per_cm is unset.
const DEFAULT_PX_PER_CM = 40;
const MAX_RADIUS_CM = 20;

export default function VirtualCompassOverlay({ visible, pxPerCm }) {
  const [radiusPx, setRadiusPx] = useState(80);
  const radiusRef = useRef(radiusPx);
  radiusRef.current = radiusPx;

  const scale = Number(pxPerCm) > 0 ? Number(pxPerCm) : DEFAULT_PX_PER_CM;
  const maxRadiusPx = scale * MAX_RADIUS_CM;
  const maxRadiusRef = useRef(maxRadiusPx);
  maxRadiusRef.current = maxRadiusPx;

  const { transform, onPointerDownMove, onPointerDownRotate } = useOverlayTransform();

  const onPointerDownRadius = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startR = radiusRef.current;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      setRadiusPx(Math.min(maxRadiusRef.current, Math.max(MIN_R, Math.round(startR + dx))));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, []);

  if (!visible) return null;

  // Sized to the CURRENT radius, not the maximum — an SVG box sized to the 20 cm cap would be a
  // full-screen drag surface swallowing clicks on the question underneath.
  const SVG_SIZE = radiusPx * 2 + PAD * 2;
  const CX = radiusPx + PAD;
  const CY = radiusPx + PAD;

  const radiusCm = (radiusPx / scale).toFixed(1);
  // Arm endpoint (horizontal arm rotated with compass)
  const armX = CX + radiusPx;
  const armY = CY;

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        left: transform.x,
        top: transform.y,
        transform: `translate(-50%, -50%) rotate(${transform.rotation}deg)`,
        transformOrigin: "center center",
        zIndex: 9999,
        pointerEvents: "auto",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
        filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.22))",
      }}
      onPointerDown={onPointerDownMove}
    >
      <svg
        width={SVG_SIZE}
        height={SVG_SIZE}
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        style={{ display: "block", overflow: "visible" }}
      >
        {/* Circle */}
        <circle
          cx={CX}
          cy={CY}
          r={radiusPx}
          fill="rgba(237,231,246,0.35)"
          stroke="#7b1fa2"
          strokeWidth="2"
          strokeDasharray="8 4"
        />
        {/* Radius arm */}
        <line x1={CX} y1={CY} x2={armX} y2={armY} stroke="#7b1fa2" strokeWidth="1.5" strokeDasharray="5 3" />
        {/* Center crosshair */}
        <line x1={CX - 14} y1={CY} x2={CX + 14} y2={CY} stroke="#6a1b9a" strokeWidth="2" />
        <line x1={CX} y1={CY - 14} x2={CX} y2={CY + 14} stroke="#6a1b9a" strokeWidth="2" />
        <circle cx={CX} cy={CY} r="4" fill="#7b1fa2" />
        {/* Radius label */}
        {radiusCm && (
          <text
            x={CX + radiusPx / 2}
            y={CY - 8}
            fill="#6a1b9a"
            fontSize="12"
            fontWeight="800"
            textAnchor="middle"
          >
            {radiusCm} cm
          </text>
        )}
        {/* Resize handle circle on the arm end */}
        <circle
          cx={armX}
          cy={armY}
          r="10"
          fill="#7b1fa2"
          stroke="white"
          strokeWidth="2"
          style={{ cursor: "ew-resize" }}
          onPointerDown={onPointerDownRadius}
        />
        <text x={armX} y={armY + 4} fill="white" fontSize="12" fontWeight="900" textAnchor="middle" style={{ pointerEvents: "none" }}>
          ⇔
        </text>
      </svg>
      <Tooltip title="Drag to rotate" placement="top">
        <Box
          onPointerDown={onPointerDownRotate}
          sx={{
            position: "absolute",
            left: -14,
            top: SVG_SIZE / 2 - 14,
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "#7b1fa2",
            color: "white",
            fontSize: "0.7rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "grab",
            border: "2px solid white",
          }}
        >
          ↻
        </Box>
      </Tooltip>
    </Box>,
    document.body
  );
}
